import React, { useEffect, useState } from 'react';

import CircularLoader from 'Components/Loaders/CircularLoader';
import SortableSearchableTable from 'Components/ui/SortableSearchableTable';
import { getDevices, getExportData } from 'Data/api';
import CheckboxGroup from 'Components/ui/CheckBoxGroup';
import DateTimeRangePickerWithPresets from 'Components/ui/DateTimeRangePickerWithPresets';
import RadioGroupSelect from 'Components/ui/RadioGroupSelect';

import { CardHeader, CardContent, Card, Container, Typography } from '@mui/material';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DropdownSelect from 'Components/ui/DropdownSelect';

const AdminExportData = () => {
	// const theme = useTheme();
	// const classes = adminStyles();

	const [loading, setLoading] = useState(true);
	const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
	const [exportDialogOpen, setExportDialogOpen] = useState(false);
	const [exportGenerating, setExportGenerating] = useState(false);
	const [deviceSelected, setDeviceSelected] = useState([]);
	const [deviceList, setDeviceList] = useState([]);
	const [dataPoints, setDataPoints] = useState(['co2m', 'humi', 'luxm', 'temp', 'tvoc', 'slevel']);
	const [dateRange, setDateRange] = useState([null, null]);
	const [timeResolution, setTimeResolution] = useState('60m');
	const [fileFormat, setFileFormat] = useState('excel');
	const [includeMeta, setIncludeMeta] = useState(true);
	const [zipLargeFiles, setZipLargeFiles] = useState(true);
	const [aggregation, setAggregation] = useState('avg');
	const [maxRangeDays, setMaxRangeDays] = useState(30);
	const [dataRangeValid, setDataRangeValid] = useState(true);

	const deviceColumns = [
		{ id: "name", label: "Navn", sortable: true },
		{ id: "title", label: "Title", sortable: true },
		{ id: "building", label: "Bygning", sortable: true },
		{ id: "description", label: "Beskrivelse", sortable: true }
	];

	// Load data
	useEffect(() => {
		const fetchData = async () => {
			let devices = await getDevices();
			console.log('AdminMoveDevices - useEffect - devices', devices);
			let newDevices = devices.map(device => ({
				id: device.uuid,
				name: device.name,
				building: device.registry ? device.registry.name : 'Unknown',
				title: device.metadata.title || 'No Title',
				description: device.metadata.description || 'No Description'
			}));
			setDeviceList(newDevices);
			setLoading(false);
		}
		fetchData();
	}, []);

	useEffect(() => {
		const maxLines = 200000; // max antal linjer vi vil tillade pr dag
		const match = timeResolution.match(/(\d+)m/);
		const bucketSize = parseInt(match[1], 10);
		let dataLines = Math.ceil((24 * 60) / bucketSize); // antal datapunkter pr dag
		let totalDataLines = dataLines * deviceSelected.length;
		let newMaxRangeDays = Math.floor(maxLines / totalDataLines);
		if (isNaN(newMaxRangeDays) || !isFinite(newMaxRangeDays) || newMaxRangeDays < 1) newMaxRangeDays = 1;
		setMaxRangeDays(newMaxRangeDays);
	}, [deviceSelected, timeResolution]);

	// Handle device dialog open/close
	const handleDeviceDialogOpen = () => {
		// console.log(theme.breakpoints);
		setDeviceDialogOpen(true);
	};
	const handleDeviceDialogClose = () => {
		setDeviceDialogOpen(false);
	};
	const handleSelectionChange = (selectedIds, selectedData) => {
		setDeviceSelected(selectedIds);
		console.log('Selected IDs:', selectedIds);
		console.log('Selected Data:', selectedData);
	};
	const downloadFileFromApiResponse = (response) => {
		const { type, filename, base64 } = response;

		// Map type til MIME type
		const mimeTypes = {
			csv: "text/csv",
			excel: "application/vnd.ms-excel",
			xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			zip: "application/zip",
		};
		const mimeType = mimeTypes[type.toLowerCase()] || "application/octet-stream";

		// Konverter base64 til Uint8Array
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);

		// Lav blob med korrekt MIME type
		const blob = new Blob([byteArray], { type: mimeType });

		// Opret URL og klik på link
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", filename || `download.${type}`);
		document.body.appendChild(link);
		link.click();

		// Cleanup
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	}

	const generateExport = async () => {

		if (deviceSelected.length === 0) {
			alert('Vælg venligst mindst én sensor for at generere en eksport.');
			return;
		}
		if (dataPoints.length === 0) {
			alert('Vælg venligst mindst ét datapunkt for at generere en eksport.');
			return;
		}
		if (!dateRange[0] || !dateRange[1] || !dataRangeValid) {
			alert('Vælg venligst en gyldig tidsperiode for at generere en eksport.');
			return;
		}
		const exportParams = {
			devices: deviceSelected,
			dataPoints,
			start: dateRange[0],
			end: dateRange[1],
			timeResolution,
			aggregation: timeResolution === '1m' ? '' : aggregation,
			fileFormat,
			includeMeta,
			zipLargeFiles,
			download: "inline"
		};
		console.log('Generating export with params:');
		console.log(exportParams);
		setExportGenerating(true);
		setExportDialogOpen(true);
		// Call API to generate export here...
		let result = await getExportData({ definition: exportParams });
		console.log('Export result:', result);
		setExportGenerating(false);
		// For now, just alert success
		// alert('Export generated! (This is a placeholder alert.)');
		downloadFileFromApiResponse(result);
	};

	return (
		!loading ?
			<>
				<Container maxWidth="lg" style={{ padding: '20px', marginBottom: '100px' }}>
					<Card>
						<CardHeader title="Vælg sensorer" />
						<CardContent>
							<Typography>Vælg hvilke sensorer, der skal eksporteres fra.</Typography>
							<Button color='admin' onClick={handleDeviceDialogOpen} variant="contained" >
								Vælg sensorer
							</Button>
							{deviceSelected.length > 0 && (
								<>
									<Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
										{deviceSelected.length} sensor(er) valgt.
									</Typography>
									<Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
										{deviceSelected.map(id => {
											const device = deviceList.find(d => d.id === id);
											// console.log('Finding name for device ID:', id, device);
											return device ? device.title + ' (' + device.name + ')' : null;
											// return device ? device.name : null;
										}).filter(name => name).join(', ')}
									</Typography>
								</>
							)}

						</CardContent>
					</Card>
					<Card style={{ marginTop: '20px' }}>
						<CardHeader title="Vælg data punkter" />
						<CardContent>
							{/* <Typography variant="body1" color="textSecondary">
								Here you can view and export device data.
							</Typography> */}
							<CheckboxGroup
								options={[
									{ label: 'CO₂, ppm', value: 'co2m' },
									{ label: 'Luftfugtighed, %', value: 'humi' },
									{ label: 'Lys, lux', value: 'luxm' },
									{ label: 'Temperatur, °C', value: 'temp' },
									{ label: 'TVOC, ppb', value: 'tvoc' },
									{ label: 'Støjniveau, dB(A)', value: 'slevel' }
								]}
								onChange={(selected) => setDataPoints(selected)}
								initialSelected={dataPoints}
								label="Vælg data punkter"
							/>
						</CardContent>
					</Card>
					<Card style={{ marginTop: '20px' }}>
						<CardHeader title="Vælg tidsperiode" />
						<CardContent>
							{/* <Typography variant="body1" color="textSecondary">
								Select the date range for the data you want to export.
							</Typography> */}
							<DateTimeRangePickerWithPresets
								onChange={(dateRange, isValid) => {
									console.log('Selected date range:', dateRange, isValid);
									setDateRange([dateRange.from, dateRange.to]);
									setDataRangeValid(isValid);
								}}
								initialFrom={dateRange[0]}
								initialTo={dateRange[1]}
								label="Vælg tidsperiode"
								maxRangeDays={maxRangeDays}
							/>
						</CardContent>
					</Card>
					<Card style={{ marginTop: '20px' }}>
						<CardHeader title="Vælg tidsopløsning" />
						<CardContent>
							{/* <Typography variant="body1" color="textSecondary">
								Here you can view and export device data.
							</Typography> */}
							<RadioGroupSelect
								options={[
									{ label: 'Aggregeret: 1 time', value: '60m' },
									{ label: 'Aggregeret: 15 min', value: '15m' },
									{ label: 'Aggregeret: 5 min', value: '5m' },
									{ label: 'Rå data: 1-minutmålinger', value: '1m' },
								]}
								onChange={(selected) => {
									setTimeResolution(selected)
									if (selected === '1m') setAggregation('')
									else if (aggregation === '') setAggregation('avg')
								}}
								initialSelected={timeResolution}
								label="Vælg tidsopløsning"
							/>
							<br />
							<DropdownSelect
								label="Vælg aggregering"
								// multiple={true}
								options={[
									{ label: "Gennemsnit", value: "avg" },
									{ label: "Min", value: "min" },
									{ label: "Max", value: "max" },
									{ label: "95. percentil", value: "p95" },
								]}
								initialSelected={aggregation}
								disabled={timeResolution === '1m'}
								onChange={(value) => setAggregation(value)}
							/>
						</CardContent>
					</Card>
					<Card style={{ marginTop: '20px' }}>
						<CardHeader title="Vælg filformat" />
						<CardContent>
							{/* <Typography variant="body1" color="textSecondary">
								Here you can view and select devices for export.
							</Typography> */}
							<DropdownSelect
								label="Vælg filformat"
								// multiple={true}
								options={[
									{ label: "Excel", value: "excel" },
									{ label: "CSV", value: "csv" },
								]}
								initialSelected="excel"
								onChange={(value) => setFileFormat(value)}
							/>
						</CardContent>
					</Card>
					<Card style={{ marginTop: '20px' }}>
						<CardHeader title="Metadata" />
						<CardContent>
							{/* <Typography variant="body1" color="textSecondary">
								Here you can view and export device data.
							</Typography> */}
							<CheckboxGroup
								options={[
									{ label: 'Inkludér metadataark (Enheder, Betegnelser, Eksport info)', value: 'meta' },
									{ label: 'ZIP eksport filen', value: 'zip' }
								]}
								initialSelected={['meta', 'zip']}
								onChange={(selected) => {
									setIncludeMeta(selected.includes('meta'));
									setZipLargeFiles(selected.includes('zip'));
								}}
								label="Metadata"
							/>
						</CardContent>
					</Card>
					<Card style={{ marginTop: '20px' }}>
						<CardHeader title="Generér eksport" />
						<CardContent>
							{/* <Typography variant="body1" color="textSecondary">
								Here you can view and export device data.
							</Typography> */}
							<Button onClick={generateExport} color='admin' variant='contained' >
								Generér eksport
							</Button>

						</CardContent>
					</Card>
				</Container>


				<Dialog open={deviceDialogOpen} fullWidth={true} maxWidth="xl" >
					<DialogTitle>Vælg sensorer</DialogTitle>
					<DialogContent>
						<><SortableSearchableTable data={deviceList} tableColumns={deviceColumns} onSelectionChange={handleSelectionChange} initialSelected={deviceSelected} /></>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleDeviceDialogClose} color="admin" variant="contained" >
							Luk
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog open={exportDialogOpen} fullWidth={true} maxWidth="sm" >
					<DialogTitle>Generér eksport</DialogTitle>
					<DialogContent>
						{/* Additional export options can be added here if needed */}
						{exportGenerating ? <><Typography>Din eksport genereres baseret på dine valgte sensorer og indstillinger.</Typography><CircularLoader /></> : (
							<><Typography>Din eksport er nu genereret. Du vil modtage en download.</Typography>
								<DialogActions>
									<Button onClick={() => setExportDialogOpen(false)} color="admin" variant="contained" >
										Luk
									</Button>
								</DialogActions>
							</>
						)}
					</DialogContent>
				</Dialog>

			</> : <CircularLoader />
	);
};

export default AdminExportData;